import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CRED_LABELS, CRED_TYPES, formatDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import MaskedValue from "@/components/MaskedValue";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Credentials() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [days, setDays] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (type) params.credential_type = type;
      if (status) params.status = status;
      if (days) params.days = Number(days);
      const r = await api.get("/credentials", { params });
      setRows(r.data);
    } catch {
      toast.error("Failed to load credentials");
    }
  };
  useEffect(() => { load(); }, [type, status, days]);

  return (
    <div className="space-y-6" data-testid="credentials-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Ledger</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Credentials</h1>
          <p className="text-sm text-gray-600 mt-2">
            Every tracked item across the ASC — filterable by type, status, and expiration window.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">All types</option>
          {CRED_TYPES.map((t) => <option key={t} value={t}>{CRED_LABELS[t]}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="EXPIRED">Expired</option>
          <option value="EXPIRING_7">≤ 7 days</option>
          <option value="EXPIRING_30">≤ 30 days</option>
          <option value="EXPIRING_60">≤ 60 days</option>
          <option value="EXPIRING_90">≤ 90 days</option>
          <option value="CURRENT">Current</option>
          <option value="NO_DATE">No date</option>
        </select>
        <select value={days} onChange={(e) => setDays(e.target.value)} className="border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="filter-days">
          <option value="">Any window</option>
          <option value="7">Within 7 days</option>
          <option value="30">Within 30 days</option>
          <option value="60">Within 60 days</option>
          <option value="90">Within 90 days</option>
        </select>
        <div className="ml-auto text-sm text-gray-500" data-testid="credentials-count">{rows.length} items</div>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="credentials-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Practitioner</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Number</th>
              <th className="px-4 py-3 font-semibold">Issuer</th>
              <th className="px-4 py-3 font-semibold">Expires</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">PSV</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="8" className="p-12 text-center text-gray-500 text-sm">No credentials match these filters.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="data-row border-b border-gray-100">
                <td className="px-6 py-3">
                  <div className="font-medium text-slate-900">{c.practitioner?.last_name}, {c.practitioner?.first_name}</div>
                  <div className="text-xs text-gray-500 font-mono">{c.practitioner?.title}</div>
                </td>
                <td className="px-4 py-3 text-slate-800">{CRED_LABELS[c.credential_type] || c.credential_type}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  <MaskedValue
                    value={c.credential_number}
                    kind={c.credential_type === "DEA" ? "dea" : "license"}
                    field={`credential_number_${c.credential_type}`}
                    resourceType="credential"
                    resourceId={c.id}
                  />
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{c.issuer || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatDate(c.expiration_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-xs font-mono">
                  <span className={`inline-block px-2 py-0.5 ${c.psv_status === "VERIFIED" ? "bg-emerald-100 text-emerald-800" : c.psv_status === "PENDING" ? "bg-sky-100 text-sky-800" : "bg-gray-100 text-gray-700"}`}>
                    {c.psv_status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/practitioners/${c.practitioner_id}`} className="text-xs uppercase tracking-wider text-slate-700 font-semibold hover:text-slate-900">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
